import { useState } from 'react';
import ClickablePseudo from '../ClickablePseudo';
import type { GameInvitePayload } from '../../types/chat';
import { GAME_INVITE_RECIPIENT_LABELS } from '../../types/chat';

interface GameInviteMessageProps {
  invite: GameInvitePayload;
  author: string;
  currentPseudo: string;
  showProposer?: boolean;
  showCancelledRecipients?: boolean;
  onRespond: (accepted: boolean) => Promise<void>;
  onCancel: () => Promise<void>;
  onAccess: () => void;
}

function formatGameMode(gameMode: string): string {
  return gameMode === 'defi_libre' ? 'Défi libre' : 'Table libre';
}

const RECIPIENTS_PREVIEW_COUNT = 4;
const RECIPIENTS_COLLAPSE_THRESHOLD = 5;

export default function GameInviteMessage({
  invite,
  author,
  currentPseudo,
  showProposer = true,
  showCancelledRecipients = true,
  onRespond,
  onCancel,
  onAccess,
}: GameInviteMessageProps) {
  const [loading, setLoading] = useState(false);
  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const current = currentPseudo.toLowerCase();
  const myRecipient = invite.recipients.find((r) => r.pseudo.toLowerCase() === current);
  const isAuthor = author.toLowerCase() === current;
  const isActive = invite.status === 'pending';
  const isOpenInvite = invite.open === true;
  const canRespond = isActive && !isAuthor && (
    isOpenInvite
      ? (!myRecipient || myRecipient.state === 'invited' || myRecipient.state === 'offline')
      : (myRecipient?.state === 'invited' || myRecipient?.state === 'offline')
  );
  const canCancel = isActive && isAuthor;
  const canAccess = isAuthor && !!invite.draftId && invite.status !== 'expired' && invite.status !== 'cancelled';

  const shouldCollapseRecipients = invite.recipients.length > RECIPIENTS_COLLAPSE_THRESHOLD;
  const visibleRecipients = shouldCollapseRecipients && !showAllRecipients
    ? invite.recipients.slice(0, RECIPIENTS_PREVIEW_COUNT)
    : invite.recipients;
  const hiddenRecipientsCount = invite.recipients.length - RECIPIENTS_PREVIEW_COUNT;

  const handleRespond = async (accepted: boolean) => {
    if (loading) return;
    setLoading(true);
    try {
      await onRespond(accepted);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await onCancel();
    } finally {
      setLoading(false);
    }
  };

  const renderRecipientsToggle = () => (
    <>
      {shouldCollapseRecipients && !showAllRecipients && (
        <button
          type="button"
          className="game-invite-recipients-toggle"
          onClick={() => setShowAllRecipients(true)}
        >
          Afficher {hiddenRecipientsCount} autre{hiddenRecipientsCount > 1 ? 's' : ''}
        </button>
      )}
      {shouldCollapseRecipients && showAllRecipients && (
        <button
          type="button"
          className="game-invite-recipients-toggle"
          onClick={() => setShowAllRecipients(false)}
        >
          Afficher moins
        </button>
      )}
    </>
  );

  const renderInlineRecipients = () => (
    <p className="game-invite-recipients-inline">
      {visibleRecipients.map((recipient, index) => (
        <span key={recipient.pseudo}>
          {index > 0 && ', '}
          <ClickablePseudo pseudo={recipient.pseudo}>{recipient.pseudoDisplay}</ClickablePseudo>
        </span>
      ))}
    </p>
  );

  if (invite.status === 'cancelled') {
    return (
      <div className={`game-invite-card status-${invite.status} game-invite-card--compact`}>
        <div className="game-invite-header">
          <span className="game-invite-icon" aria-hidden="true">🎮</span>
          <div className="game-invite-title-wrap">
            <span className="game-invite-title">Invitation annulée</span>
            <span className="game-invite-subtitle">
              {formatGameMode(invite.config.gameMode)} · {invite.config.nombreJoueurs} joueurs
            </span>
          </div>
        </div>

        {showProposer && (
          <div className="game-invite-author">
            Proposée par <ClickablePseudo pseudo={author}>{author}</ClickablePseudo>
          </div>
        )}

        {showCancelledRecipients && (
          <>
            {renderInlineRecipients()}
            {renderRecipientsToggle()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`game-invite-card status-${invite.status}`}>
      <div className="game-invite-header">
        <span className="game-invite-icon" aria-hidden="true">🎮</span>
        <div className="game-invite-title-wrap">
          <span className="game-invite-title">Invitation à jouer</span>
          <span className="game-invite-subtitle">
            {formatGameMode(invite.config.gameMode)} · {invite.config.nombreJoueurs} joueurs
          </span>
        </div>
      </div>

      {showProposer && (
        <div className="game-invite-author">
          Proposée par <ClickablePseudo pseudo={author}>{author}</ClickablePseudo>
        </div>
      )}

      {invite.recipients.length > 0 ? (
        <ul className="game-invite-recipients">
          {visibleRecipients.map((recipient) => (
            <li key={recipient.pseudo} className={`recipient-state-${recipient.state}`}>
              <ClickablePseudo pseudo={recipient.pseudo}>{recipient.pseudoDisplay}</ClickablePseudo>
              <span className="game-invite-recipient-state">
                {GAME_INVITE_RECIPIENT_LABELS[recipient.state]}
              </span>
            </li>
          ))}
        </ul>
      ) : isOpenInvite && isActive ? (
        <p className="game-invite-footnote">Ouverte à tout le monde</p>
      ) : null}

      {renderRecipientsToggle()}

      {invite.status === 'expired' && (
        <p className="game-invite-footnote">Cette invitation n'est plus valable.</p>
      )}

      {canRespond && (
        <div className="game-invite-actions">
          <button
            type="button"
            className="game-invite-btn accept"
            disabled={loading}
            onClick={() => handleRespond(true)}
          >
            Accepter
          </button>
          <button
            type="button"
            className="game-invite-btn decline"
            disabled={loading}
            onClick={() => handleRespond(false)}
          >
            Refuser
          </button>
        </div>
      )}

      {(canAccess || canCancel) && (
        <div className="game-invite-actions">
          {canAccess && (
            <button
              type="button"
              className="game-invite-btn access"
              disabled={loading}
              onClick={onAccess}
            >
              Accéder
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              className="game-invite-btn cancel"
              disabled={loading}
              onClick={handleCancel}
            >
              Annuler
            </button>
          )}
        </div>
      )}
    </div>
  );
}
